import {
  TBitValue,
  TReactSetState,
  TUseTranslationTfn,
} from "../../data/AppType";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
} from "../../utils/AppFunctions";
import * as Yup from "yup";
import {
  DEFAULT_DUPLICATE_LOOKUP_KEY,
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  CREATE_LOOKUP,
  GET_LOOKUP_BY_ID,
  RESTORE_LOOKUP,
  UPDATE_LOOKUP,
} from "../../services/Endpoints";

export interface ILookupForm {
  id?: string;
  display_value: string;
  data_value: string;
  lookup_category: string;
  sort_order: number | null;
  is_active: TBitValue;
}

export interface IRestoreValue {
  id: string;
  event_key: string;
  existing_record_data_value: string;
  existing_record_display_value: string;
}

export const lookupValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    display_value: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("display_value")],
        ])
      )
      .max(
        50,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("display_value")],
          ["max", "50"],
        ])
      )
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("display_value")],
          ["min", "2"],
        ])
      ),

    data_value: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("data_value")],
        ])
      )
      .max(
        50,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("data_value")],
          ["max", "50"],
        ])
      ),
    lookup_category: Yup.string().required(
      prepareMessageFromParams(t("error_message_required"), [
        ["fieldName", t("lookup_category")],
      ])
    ),
    sort_order: Yup.number()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("sort_order")],
        ])
      )
      .max(
        100,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("sort_order")],
          ["max", "100"],
        ])
      )
      .min(
        1,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("sort_order")],
          ["min", "1"],
        ])
      ),
  });

export const LOOKUP_INITIAL_VALUES = {
  display_value: "",
  data_value: "",
  lookup_category: "",
  sort_order: null,
  is_active: BIT_VALUE.One,
};

export const fetchLookupId = async (
  id: string,
  handleClose: (status: boolean) => void,
  setLookup: TReactSetState<ILookupForm | undefined>
) => {
  try {
    const lookupData = await GET_LOOKUP_BY_ID(id);
    if (lookupData && lookupData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setLookup(lookupData.data);
    } else {
      handleClose(false);
      sweetAlertError({
        message: lookupData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleClose(false);
    handleError(e, "fetchLookupId");
  }
};

export const handleSubmitLookup = async (
  values: ILookupForm,
  setLoading: TReactSetState<boolean>,
  id: string | undefined,
  handleClose: (status: boolean) => void,
  setDuplicateValues: TReactSetState<IRestoreValue | undefined>,
  setSubmitValue: TReactSetState<ILookupForm | undefined>
) => {
  setLoading(true);
  try {
    let lookup;
    if (id) {
      lookup = await UPDATE_LOOKUP(values, id);
    } else {
      lookup = await CREATE_LOOKUP(values);
    }
    if (lookup && lookup.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({
        message: lookup.message,
      });
      return handleClose(true);
    } else if (lookup?.data?.data?.event_key === DEFAULT_DUPLICATE_LOOKUP_KEY) {
      setDuplicateValues(lookup.data.data);
      setSubmitValue(values);
    } else {
      sweetAlertError({
        message: lookup?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleError(e, "handleSubmitLookup");
  } finally {
    setLoading(false);
  }
};

export const restoreLookup = async (
  id: string,
  values: ILookupForm,
  handleClose: (status: boolean) => void
) => {
  try {
    const lookupData = await RESTORE_LOOKUP(id, values);
    if (lookupData && lookupData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({
        message: lookupData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return handleClose(true);
    } else {
      sweetAlertError({
        message: lookupData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleError(e, "restoreLookup");
  }
};
